import { html, type TemplateResult } from "lit";

import "../status-badge/status-badge";

import { WowBaseTable } from "./wow-base-table";
import type { SortDirection } from "./types";
import type { ISortableState, ISortableService } from "./sort-types";

export abstract class WowSortableTable<T> extends WowBaseTable<T> {
  protected abstract getSortState(): ISortableState<T>;
  protected abstract getSortService(): ISortableService;
  protected abstract getItems(): T[];

  override connectedCallback() {
    super.connectedCallback();
    this.addEventListener("sort-change", this.handleSortChange as EventListener);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("sort-change", this.handleSortChange as EventListener);
  }

  override willUpdate(changed: Map<string, unknown>) {
    super.willUpdate(changed);

    const sortState = this.getSortState();
    this.data = sortState.getSortedItems(this.getItems());

    const sorts = sortState.activeSorts.get();
    const activeStates: Record<string, SortDirection> = {};
    for (const { column, direction } of sorts) {
      activeStates[column] = direction;
    }
    this.sortStates = activeStates;
  }

  private handleSortChange = (e: Event) => {
    const { column, direction } = (e as CustomEvent<{ column: string; direction: SortDirection }>).detail;
    this.getSortService().updateSort(column, direction);
    this.requestUpdate();
  };

  protected get locale(): string {
    return localStorage.getItem("language") ?? navigator.language;
  }

  protected renderDate(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleDateString(this.locale, { dateStyle: "long" });
  }

  protected renderTime(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleTimeString(this.locale, { timeStyle: "short" });
  }

  protected renderStatusBadge(
    status: string,
    labelMap: Record<string, string | TemplateResult>,
    variantMap: Record<string, string>,
    pulseStatuses: string[] = [],
  ): TemplateResult {
    const variant = variantMap[status] ?? "event";
    const label = labelMap[status] ?? status;
    return html`<status-badge variant=${variant} ?pulse=${pulseStatuses.includes(status)}>${label}</status-badge>`;
  }

  protected emit(name: string, detail?: unknown) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }
}
